import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { calculateProductQuantity } from '../../../Services/quoteCalculator'

const BardageProduct = ({ quoteData }) => {
  // Récupérer les données depuis Redux
  const selectedProducts = useSelector(state => state.selectedProducts.selectedProducts);
  const selectedProject = useSelector(state => state.selectedProject.selectedProject);
  const sectionNames = useSelector(state => state.selectedProducts.sectionNames);
  const userInputs = useSelector(state => state.selectedProducts.userInputs);
  const selectedChoices = useSelector(state => state.selectedProducts.selectedChoices);
  const surfaceArea = useSelector(state => state.selectedProducts.surfaceArea);
  const projectType = useSelector(state => state.selectedProducts.projectType) || selectedProject?.project_type || 'BARDAGE';

  // Déterminer le type de bardage sélectionné
  const bardageType = useMemo(() => {
    const lamesBois = selectedChoices.find(c => c.code === 'TYPE_LAMES_BOIS');
    const lamesFibresCiment = selectedChoices.find(c => c.code === 'TYPE_LAMES_FIBRES_CIMENT');
    const panneauxFibresCiment = selectedChoices.find(c => c.code === 'TYPE_PANNEAUX_FIBRES_CIMENT');
    return lamesBois ? 'LAMES_BOIS' : (lamesFibresCiment ? 'LAMES_FIBRES_CIMENT' : (panneauxFibresCiment ? 'PANNEAUX_FIBRES_CIMENT' : null));
  }, [selectedChoices]);

  // Déterminer l'orientation (HORIZONTALE ou VERTICALE)
  const orientation = useMemo(() => {
    const horizontal = selectedChoices.find(c => c.code === 'SENS_POSE_HORIZONTALE');
    const vertical = selectedChoices.find(c => c.code === 'SENS_POSE_VERTICALE');
    return horizontal ? 'HORIZONTALE' : (vertical ? 'VERTICALE' : null);
  }, [selectedChoices]);

  // Calculer la surface totale depuis les inputs
  const calculatedSurface = useMemo(() => {
    let totalSurface = surfaceArea || 0;
    
    // Parcourir tous les inputs pour trouver les surfaces
    Object.entries(userInputs || {}).forEach(([sectionId, sectionInputs]) => {
      Object.entries(sectionInputs).forEach(([key, inputData]) => {
        const value = typeof inputData === 'object' && inputData?.value !== undefined 
          ? inputData.value 
          : inputData;
        const label = typeof inputData === 'object' && inputData?.label 
          ? inputData.label.toLowerCase() 
          : key.toLowerCase();
        
        // Chercher les valeurs de surface
        if (label.includes('surface') || label.includes('m²') || label.includes('m2') || label.includes('bardage')) {
          const numValue = parseFloat(value);
          if (!isNaN(numValue) && numValue > 0) {
            totalSurface += numValue;
          }
        }
      });
    });
    
    return totalSurface;
  }, [userInputs, surfaceArea]);

  // Liste de tous les produits avec leurs codes et formules selon le type de bardage
  const allProducts = useMemo(() => {
    if (!bardageType) return [];

    const products = [];

    // Produits communs à tous les types de bardage
    products.push(
      { code: 'LAMBOURDE', name: 'Lambourde', formula: orientation === 'VERTICALE' ? 'Surface x 2 (ML)' : 'Surface x 1.5 (ML)', unit: 'ML' },
      { code: 'VISERIE', name: 'Visserie', formula: orientation === 'VERTICALE' ? 'Surface x 25 (UN)' : 'Surface x 20 (UN)', unit: 'UN' }
    );

    // Produits spécifiques selon le type de bardage
    if (bardageType === 'LAMES_BOIS') {
      products.push(
        { code: 'BARDAGE_LAMES_BOIS', name: 'Lames de bardage bois', formula: 'Surface + 7% (taux de chute)', unit: 'M²' },
        { code: 'ECRAN_PLUIE', name: 'Écran de pluie', formula: 'Surface (m²)', unit: 'M²' },
        { code: 'EPDM', name: 'EPDM', formula: 'Surface (m²)', unit: 'M²' },
        { code: 'CORNIERE', name: 'Cornière', formula: 'Périmètre (ML)', unit: 'ML' },
        { code: 'GRILLE_VENTILATION', name: 'Grille de ventilation', formula: '1 par 10 m²', unit: 'UN' },
        { code: 'PROFIL_DEPART', name: 'Profil de départ', formula: 'Périmètre (ML)', unit: 'ML' },
        { code: 'PROFIL_FIN', name: 'Profil de fin', formula: 'Périmètre (ML)', unit: 'ML' },
        { code: 'PEINTURE_RETOUCHE', name: 'Peinture de retouche', formula: '1 UN', unit: 'UN' }
      );
    } else if (bardageType === 'LAMES_FIBRES_CIMENT') {
      products.push(
        { code: 'BARDAGE_LAMES_FIBRES_CIMENT', name: 'Lames de bardage fibres-ciment', formula: 'Surface + 5% (taux de chute)', unit: 'M²' },
        { code: 'ECRAN_PLUIE', name: 'Écran de pluie', formula: 'Surface (m²)', unit: 'M²' },
        { code: 'EPDM', name: 'EPDM', formula: 'Surface (m²)', unit: 'M²' },
        { code: 'CORNIERE', name: 'Cornière', formula: 'Périmètre (ML)', unit: 'ML' },
        { code: 'GRILLE_VENTILATION', name: 'Grille de ventilation', formula: '1 par 10 m²', unit: 'UN' },
        { code: 'PROFIL_DEPART', name: 'Profil de départ', formula: 'Périmètre (ML)', unit: 'ML' },
        { code: 'PROFIL_FIN', name: 'Profil de fin', formula: 'Périmètre (ML)', unit: 'ML' }
      );
    } else if (bardageType === 'PANNEAUX_FIBRES_CIMENT') {
      products.push(
        { code: 'BARDAGE_PANNEAUX_FIBRES_CIMENT', name: 'Panneaux de bardage fibres-ciment', formula: 'Surface + 5% (taux de chute)', unit: 'M²' },
        { code: 'ECRAN_PLUIE', name: 'Écran de pluie', formula: 'Surface (m²)', unit: 'M²' },
        { code: 'EPDM', name: 'EPDM', formula: 'Surface (m²)', unit: 'M²' },
        { code: 'CORNIERE', name: 'Cornière', formula: 'Périmètre (ML)', unit: 'ML' },
        { code: 'GRILLE_VENTILATION', name: 'Grille de ventilation', formula: '1 par 10 m²', unit: 'UN' }
      );
    }

    return products;
  }, [bardageType, orientation]);

  // Calculer les quantités pour tous les produits
  const calculatedProducts = useMemo(() => {
    if (!bardageType || calculatedSurface <= 0) return [];

    return allProducts.map(product => {
      const quantity = calculateProductQuantity(
        product.code,
        projectType,
        calculatedSurface,
        userInputs || {},
        selectedChoices || []
      );
      
      return {
        ...product,
        quantity: quantity > 0 ? quantity : 0
      };
    }).filter(p => p.quantity > 0); // Filtrer les produits avec quantité 0
  }, [allProducts, bardageType, calculatedSurface, projectType, userInputs, selectedChoices]);

  // Préparer les items à afficher
  const itemsToDisplay = quoteData?.items || selectedProducts || [];

  // Fonction pour récupérer les détails du produit
  const getProductDetails = (item) => {
    if (item.name && item.price !== undefined) {
      return {
        name: item.name,
        image: item.image || "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
        price: item.price,
        quantity: item.quantity || 0,
        sku: item.sku || item.product_sku || ''
      };
    }
    
    if (item.product_name) {
      return {
        name: item.product_name,
        image: item.product_image || "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
        price: parseFloat(item.product_price) || 0,
        quantity: parseInt(item.quantity) || 0,
        sku: item.product_sku || item.sku || ''
      };
    }
    
    return {
      name: 'Produit inconnu',
      image: "/assets/images/lalliard-lame-terrasse-focus_pin_du_nord_marron.png",
      price: 0,
      quantity: 0,
      sku: ''
    };
  };

  // Fonction pour calculer la quantité selon les formules BARDAGE
  const mapBardageProductToCode = (productName, sku, productType) => {
    if (!productName && !sku && !productType) return null;

    const nameLower = String(productName || '').toLowerCase();
    const skuLower = String(sku || '').toLowerCase();
    const typeLower = String(productType || '').toLowerCase();
    const searchText = `${nameLower} ${skuLower} ${typeLower}`;

    // IMPORTANT: vérifier les cas spécifiques avant les génériques
    if (searchText.includes('peinture') && searchText.includes('retouch')) return 'PEINTURE_RETOUCHE';
    if (searchText.includes('profil') && searchText.includes('depart')) return 'PROFIL_DEPART';
    if (searchText.includes('profil') && searchText.includes('fin')) return 'PROFIL_FIN';
    if (searchText.includes('grille') && (searchText.includes('rongeur') || searchText.includes('insect') || searchText.includes('vent'))) return 'GRILLE_VENTILATION';
    if (searchText.includes('corniere') || searchText.includes('cornière')) return 'CORNIERE';
    if (searchText.includes('epdm')) return 'EPDM';
    if (searchText.includes('ecran') && searchText.includes('pluie')) return 'ECRAN_PLUIE';

    // Types de bardage
    if ((searchText.includes('bardage') || searchText.includes('clin') || searchText.includes('claire voie')) && searchText.includes('bois')) return 'BARDAGE_LAMES_BOIS';
    if (searchText.includes('bardage') && searchText.includes('fibres') && searchText.includes('ciment')) return 'BARDAGE_LAMES_FIBRES_CIMENT';
    if (searchText.includes('panneau') && searchText.includes('fibres') && searchText.includes('ciment')) return 'BARDAGE_PANNEAUX_FIBRES_CIMENT';

    // Structure
    if (searchText.includes('lambourde')) return 'LAMBOURDE';
    if (searchText.includes('vis')) return 'VISERIE';

    return null;
  };

  const getCalculatedQuantity = (item) => {
    const pd = getProductDetails(item);
    const sku = pd.sku || item.sku || item.product_sku || '';
    const productType = item.productType || item.product_type || '';
    const productName = pd.name || '';

    let formulaCode = mapBardageProductToCode(productName, sku, productType);
    console.log(`🔍 Bardage mapping produit "${productName}":`, { sku, productType, formulaCode: formulaCode || 'non trouvé' });

    if (!formulaCode) {
      formulaCode = sku || item.code || productType || productName || '';
      console.log('🔍 Bardage utilisation code direct:', formulaCode);
    }

    if (!formulaCode) {
      return pd.quantity || 1;
    }
    
    const calculatedQty = calculateProductQuantity(
      formulaCode,
      projectType,
      calculatedSurface,
      userInputs || {},
      selectedChoices || []
    );
    
    return calculatedQty;
  };

  return (
    <div>
      <div className="page">
        <div className="container">
          <div className="row px-5">
            <div className="col-md-5 col-sm-5 col-12 px-0">
              <div className="image-terrasse" style={{ height: 380 }}>
                <img
                  src="/assets/images/Capture_terrace.jpg.png"
                  alt=""
                  className="w-100 h-100"
                />
              </div>
            </div>
            <div className="col-md-7 col-sm-7 col-12 px-0">
              {/* Affichage des calculs selon les formules du PDF */}
              {bardageType && calculatedSurface > 0 ? (
                <div>
                  {/* Tableau des calculs détaillés */}
                  <div className="mt-4 mb-4">
                    <h4 className="mb-3">Calculs détaillés (formule {bardageType})</h4>
                    {orientation && (
                      <p className="text-muted mb-3">Orientation: {orientation}</p>
                    )}
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Produit</th>
                            <th>Calcul</th>
                            <th>Quantité</th>
                          </tr>
                        </thead>
                        <tbody>
                          {calculatedProducts.map((product, idx) => (
                            <tr key={`calc-${idx}`}>
                              <td>{product.name}</td>
                              <td>{product.formula}</td>
                              <td>{product.quantity} {product.unit}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Affichage des produits sélectionnés si disponibles */}
                  {itemsToDisplay.length > 0 && (
                    <div className="mt-5">
                      <h4 className="mb-3">Produits sélectionnés et soumis</h4>
                      <div className="table-responsive">
                        <table className="table table-bordered">
                          <thead>
                            <tr>
                              <th>Produit</th>
                              <th>Prix unitaire (€)</th>
                              <th>Quantité</th>
                              <th>Total (€)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemsToDisplay.map((item, idx) => {
                              const pd = getProductDetails(item);
                              const computedQty = getCalculatedQuantity(item);
                              const lineTotal = (pd.price || 0) * (computedQty || 0);
                              
                              return (
                                <tr key={`summary-${idx}`}>
                                  <td>{pd.name}</td>
                                  <td>{pd.price ? pd.price.toFixed(2) : 'N/A'}</td>
                                  <td>{computedQty}</td>
                                  <td>{lineTotal.toFixed(2)}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr>
                              <th colSpan={3} className="text-end">Total</th>
                              <th>
                                {(() => {
                                  const total = itemsToDisplay.reduce((sum, item) => {
                                    const pd = getProductDetails(item);
                                    const computedQty = getCalculatedQuantity(item);
                                    return sum + ((pd.price || 0) * (computedQty || 0));
                                  }, 0);
                                  return total.toFixed(2);
                                })()} €
                              </th>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-muted">
                    {!bardageType 
                      ? 'Veuillez sélectionner un type de bardage (Lames bois, Lames fibres-ciment ou Panneaux fibres-ciment) pour voir les calculs.'
                      : calculatedSurface <= 0
                      ? 'Veuillez remplir les dimensions du projet pour voir les calculs.'
                      : 'Aucun calcul disponible.'}
                  </p>
                </div>
              )}
              
              {/* Bouton retour à l'accueil */}
              <div className="mt-4 mb-4 px-4">
                <Link to="/" className="btn btn-outline-secondary rounded-0">
                  <span className="text-2">Retour à l'accueil</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BardageProduct

